
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.currentIndex = 0;
        this.touchX = 0;
        this.touchY = 0;

        this.onKeyNavigation =
          this.onKeyNavigation.bind(this);

        this.onTouchStart =
          this.onTouchStart.bind(this);

        this.onTouchEnd =
          this.onTouchEnd.bind(this);

        // Prevent Dawn from closing the modal
        // when navigation arrows are clicked.
        this.addEventListener(
          'pointerup',
          (event) => {
            if (event.target.closest('.wm-gallery-nav')) {
              event.stopImmediatePropagation();
            }
          },
          true
        );
      }

      connectedCallback() {
        super.connectedCallback();

        if (this.wmInitialized) return;
        this.wmInitialized = true;

        this.classList.add('wm-gallery-modal');

        this.installStyles();
        this.buildNavigation();

        document.addEventListener(
          'keydown',
          this.onKeyNavigation
        );

        const content = this.querySelector(
          '.product-media-modal__content'
        );

        if (content) {
          content.addEventListener(
            'touchstart',
            this.onTouchStart,
            { passive: true }
          );

          content.addEventListener(
            'touchend',
            this.onTouchEnd,
            { passive: true }
          );
        }
      }

      disconnectedCallback() {
        document.removeEventListener(
          'keydown',
          this.onKeyNavigation
        );
      }

      getItems() {
        const content = this.querySelector(
          '.product-media-modal__content'
        );

        if (!content) return [];

        return Array.from(content.children).filter(
          (item) => item.hasAttribute('data-media-id')
        );
      }

      show(opener) {
        super.show(opener);

        const items = this.getItems();

        const mediaId = opener
          ? opener.getAttribute('data-media-id')
          : null;

        const index = items.findIndex(
          (item) =>
            item.getAttribute('data-media-id') === mediaId
        );

        this.currentIndex = index >= 0 ? index : 0;

        this.showItem(this.currentIndex);
      }

      hide() {
        super.hide();
      }

      showItem(index) {
        const items = this.getItems();

        if (!items.length) return;

        this.currentIndex =
          (index + items.length) % items.length;

        window.pauseAllMedia?.();

        items.forEach((item, i) => {
          const active = i === this.currentIndex;

          item.classList.toggle('active', active);

          item.setAttribute(
            'aria-hidden',
            active ? 'false' : 'true'
          );
        });

        const active = items[this.currentIndex];

        const content = this.querySelector(
          '.product-media-modal__content'
        );

        if (content) {
          content.scrollTop = 0;
          content.scrollLeft = 0;
        }

        // Preserve Dawn's deferred video behavior.
        if (
          active.tagName === 'DEFERRED-MEDIA' &&
          active.querySelector('template')
        ) {
          if (!active.hasAttribute('loaded')) {
            active.loadContent();
          }
        }

        this.updateNavigation();
      }

      next() {
        this.showItem(this.currentIndex + 1);
      }

      previous() {
        this.showItem(this.currentIndex - 1);
      }

      buildNavigation() {
        if (this.querySelector('.wm-gallery-nav')) return;

        const dialog = this.querySelector(
          '.product-media-modal__dialog'
        );

        if (!dialog) return;

        const nav = document.createElement('div');

        nav.className = 'wm-gallery-nav';

        nav.innerHTML = `
          <button type="button"
            class="wm-gallery-arrow wm-gallery-prev"
            aria-label="Previous image">
            &#10094;
          </button>
          <button type="button"
            class="wm-gallery-arrow wm-gallery-next"
            aria-label="Next image">
            &#10095;
          </button>
        `;

        dialog.appendChild(nav);

        const prev = nav.querySelector(
          '.wm-gallery-prev'
        );
        const next = nav.querySelector(
          '.wm-gallery-next'
        );

        [prev, next].forEach((button) => {
          button.addEventListener(
            'pointerdown',
            (event) => {
              event.stopPropagation();
            }
          );

          button.addEventListener(
            'pointerup',
            (event) => {
              event.stopPropagation();
            }
          );
        });

        prev.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.previous();
        });

        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.next();
        });
      }

      updateNavigation() {
        const nav = this.querySelector('.wm-gallery-nav');

        if (nav) {
          nav.style.display =
            this.getItems().length > 1 ? 'block' : 'none';
        }
      }

      onKeyNavigation(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous();
        }
      }

      onTouchStart(event) {
        if (event.touches.length !== 1) return;

        this.touchX = event.touches[0].clientX;
        this.touchY = event.touches[0].clientY;
      }

      onTouchEnd(event) {
        if (!this.hasAttribute('open')) return;
        if (!event.changedTouches.length) return;

        const dx =
          event.changedTouches[0].clientX - this.touchX;

        const dy =
          event.changedTouches[0].clientY - this.touchY;

        if (
          Math.abs(dx) < 55 ||
          Math.abs(dx) < Math.abs(dy) * 1.3
        ) return;

        if (dx < 0) {
          this.next();
        } else {
          this.previous();
        }
      }

      installStyles() {
        if (document.getElementById('wm-gallery-styles')) {
          return;
        }

        const style = document.createElement('style');
        style.id = 'wm-gallery-styles';

        style.textContent = `
          .wm-gallery-modal .product-media-modal__dialog {
            height: 100%;
            max-height: 100%;
            overflow: hidden;
          }

          .wm-gallery-modal .product-media-modal__content {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            padding: 60px 64px !important;
            margin: 0 !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }

          .wm-gallery-modal
          .product-media-modal__content > [data-media-id] {
            display: none !important;
          }

          .wm-gallery-modal
          .product-media-modal__content > [data-media-id].active {
            display: block !important;
            width: auto !important;
            max-width: 100% !important;
            max-height: 100% !important;
            margin: auto !important;
            flex: 0 1 auto !important;
          }

          .wm-gallery-modal
          .product-media-modal__content > img.active {
            width: auto !important;
            height: auto !important;
            max-width: 100% !important;
            max-height: 100% !important;
            object-fit: contain !important;
          }

          .wm-gallery-modal
          .product-media-modal__content > deferred-media.active,
          .wm-gallery-modal
          .product-media-modal__content > .product-media-modal__model.active {
            width: min(100%, 900px) !important;
            height: auto !important;
          }

          .wm-gallery-modal .wm-gallery-arrow {
            position: fixed;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 44px;
            height: 44px;
            padding: 0;
            border: 1px solid #e5e5e5;
            border-radius: 50%;
            background: #fff;
            color: #222;
            font-size: 22px;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,.1);
          }

          .wm-gallery-prev {
            left: 12px;
          }

          .wm-gallery-next {
            right: 12px;
          }

          .wm-gallery-modal
          .product-media-modal__toggle {
            z-index: 12 !important;
          }

          @media screen and (max-width: 749px) {
            .wm-gallery-modal
            .product-media-modal__content {
              width: 100vw !important;
              height: 100dvh !important;
              max-height: 100dvh !important;
              padding: 65px 46px 50px !important;
            }

            .wm-gallery-modal .wm-gallery-arrow {
              width: 34px;
              height: 34px;
              font-size: 17px;
            }

            .wm-gallery-prev {
              left: 5px;
            }

            .wm-gallery-next {
              right: 5px;
            }
          }
        `;

        document.head.appendChild(style);
      }
    }
  );
}
